import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { 
  ArrowLeft, 
  Heart, 
  MessageCircle, 
  Eye, 
  Clock,
  Users,
  Pin,
  Share2,
  MoreHorizontal,
  ThumbsUp,
  Send,
  Edit,
  Trash2,
  X
} from 'lucide-react'

const Container = styled.div`
  padding: 40px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  max-width: 900px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: ${props => props.theme.cardBackground};
  border: 2px solid ${props => props.theme.border};
  border-radius: 10px;
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    background-color: ${props => props.theme.hoverBackground};
  }
`

const PostContainer = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 30px;
`

const PostHeader = styled.div`
  padding: 30px;
  border-bottom: 1px solid ${props => props.theme.border};
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const PostTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 20px 0;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const PostBadge = styled.span`
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => 
    props.$type === 'notice' ? 'rgba(255, 107, 107, 0.1)' :
    props.$type === 'hot' ? 'rgba(255, 193, 7, 0.1)' :
    props.$type === 'idea' ? 'rgba(76, 175, 80, 0.1)' :
    props.$type === 'bug' ? 'rgba(255, 87, 34, 0.1)' :
    'rgba(33, 150, 243, 0.1)'
  };
  color: ${props => 
    props.$type === 'notice' ? '#ff6b6b' :
    props.$type === 'hot' ? '#ff9800' :
    props.$type === 'idea' ? '#4caf50' :
    props.$type === 'bug' ? '#ff5722' :
    '#2196f3'
  };
`

const PostMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
`

const MetaLeft = styled.div`
  display: flex;
  gap: 20px;
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  flex-wrap: wrap;
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const MetaRight = styled.div`
  display: flex;
  gap: 10px;
  position: relative;
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  background-color: ${props => props.theme.hoverBackground};
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimary}20;
    color: ${props => props.theme.buttonPrimary};
  }
  
  &.active {
    background-color: ${props => props.theme.buttonPrimary};
    color: white;
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 120px;
  overflow: hidden;
  margin-top: 8px;
`

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  &.delete {
    color: #ff5722;
    
    &:hover {
      background-color: rgba(255, 87, 34, 0.1);
    }
  }
`

const PostContent = styled.div`
  padding: 30px;
  color: ${props => props.theme.textPrimary};
  font-size: 16px;
  line-height: 1.8;
  
  @media (max-width: 768px) {
    padding: 20px;
    font-size: 15px;
  }
  
  p {
    margin: 0 0 20px 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
    margin: 20px 0;
  }
  
  blockquote {
    background-color: ${props => props.theme.hoverBackground};
    border-left: 4px solid ${props => props.theme.buttonPrimary};
    padding: 16px 20px;
    margin: 20px 0;
    border-radius: 0 8px 8px 0;
    font-style: italic;
  }
`

const PostActions = styled.div`
  padding: 20px 30px;
  border-top: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 15px 20px;
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
`

const ActionsLeft = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 768px) {
    justify-content: space-around;
  }
`

const LikeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 25px;
  border: 2px solid ${props => props.$liked ? props.theme.buttonPrimary : props.theme.border};
  background-color: ${props => props.$liked ? props.theme.buttonPrimary : props.theme.cardBackground};
  color: ${props => props.$liked ? 'white' : props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.border};
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
    color: ${props => props.theme.buttonPrimary};
  }
`

const CommentsSection = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const CommentsHeader = styled.div`
  padding: 25px 30px 20px;
  border-bottom: 1px solid ${props => props.theme.border};
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const CommentsTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`

const CommentWriteSection = styled.div`
  padding: 20px 30px;
  border-bottom: 1px solid ${props => props.theme.border};
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 16px 20px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 12px;
  font-size: 14px;
  background-color: ${props => props.theme.backgroundColor};
  color: ${props => props.theme.textPrimary};
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
  margin-bottom: 15px;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    opacity: 0.7;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 3px ${props => props.theme.buttonPrimary}20;
  }
`

const CommentSubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  margin-left: auto;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

const CommentsList = styled.div`
  padding: 0 30px 30px;
  
  @media (max-width: 768px) {
    padding: 0 20px 20px;
  }
`

const CommentItem = styled.div`
  padding: 20px 0;
  border-bottom: 1px solid ${props => props.theme.border};
  
  &:last-child {
    border-bottom: none;
  }
`

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const CommentAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${props => props.theme.textPrimary};
  font-weight: 600;
  font-size: 14px;
`

const CommentMeta = styled.div`
  display: flex;
  gap: 15px;
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
`

const CommentContent = styled.p`
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 12px 0;
`

const CommentActions = styled.div`
  display: flex;
  gap: 15px;
`

const CommentActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background-color: transparent;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    color: ${props => props.theme.textPrimary};
  }
  
  &.liked {
    color: ${props => props.theme.buttonPrimary};
  }
`

const ReplySection = styled.div`
  margin-top: 15px;
  padding-left: 20px;
  border-left: 3px solid ${props => props.theme.border};
`

const RepliesContainer = styled.div`
  margin-top: 15px;
  padding-left: 30px;
  border-left: 2px solid ${props => props.theme.border};
  
  @media (max-width: 768px) {
    padding-left: 20px;
  }
`

const ReplyItem = styled.div`
  padding: 15px 0;
  border-bottom: 1px solid ${props => props.theme.border};
  
  &:last-child {
    border-bottom: none;
  }
`

const ReplyInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px 16px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`

const ReplyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
`

const ReplyButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.primary {
    background-color: ${props => props.theme.buttonPrimary};
    color: white;
    
    &:hover {
      background-color: ${props => props.theme.buttonPrimaryHover};
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: ${props => props.theme.textSecondary};
    border: 1px solid ${props => props.theme.border};
    
    &:hover {
      background-color: ${props => props.theme.hoverBackground};
    }
  }
`

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  color: ${props => props.theme.textSecondary};
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`

const ModalContent = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`

const ModalHeader = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
`

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const ModalBody = styled.div`
  padding: 30px;
`

const FormGroup = styled.div`
  margin-bottom: 25px;
`

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 8px;
`

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 16px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`

const FormTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 16px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`

const FormSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 16px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const ModalFooter = styled.div`
  padding: 20px 30px;
  border-top: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`

const ModalButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background-color: ${props => props.theme.buttonPrimary};
    color: white;
    
    &:hover:not(:disabled) {
      background-color: ${props => props.theme.buttonPrimaryHover};
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: ${props => props.theme.textSecondary};
    border: 1px solid ${props => props.theme.border};
    
    &:hover {
      background-color: ${props => props.theme.hoverBackground};
    }
  }
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.textSecondary};
  
  h3 {
    color: ${props => props.theme.textPrimary};
    margin-bottom: 10px;
  }
`

export default function CommunityDetailPage() {
  const { theme } = useTheme()
  const { isLoggedIn, user } = useAuth()
  const navigate = useNavigate()
  const { id } = useParams()
  
  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [liked, setLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null) // 답글을 작성 중인 댓글 ID
  const [replyContent, setReplyContent] = useState('') // 답글 내용
  const [submittingReply, setSubmittingReply] = useState(false)
  // 수정 모달 관련 상태
  const [showEditModal, setShowEditModal] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editContent, setEditContent] = useState('')
  const [editCategory, setEditCategory] = useState('')
  const [updatingPost, setUpdatingPost] = useState(false)

  // 게시글 데이터 가져오기
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true)
        console.log('📚 게시글 상세 데이터 로드 시작...')

        const userId = user?.id || '';
        const response = await fetch(`/community/posts/${id}${userId ? `?userId=${userId}` : ''}`)
        console.log('📡 응답 상태:', response.status, response.statusText)
        
        if (!response.ok) {
          throw new Error(`HTTP 오류: ${response.status}`)
        }
        
        const result = await response.json()
        console.log('📋 서버 응답:', result)
        
        if (result.success) {
          console.log('✅ 게시글 로드 성공')
          setPost(result.post)
          setComments(result.comments || [])
          setLikeCount(result.post.like_count || 0)
          setLiked(result.post.is_liked || false)
        } else {
          console.error('❌ 게시글 로드 실패:', result.message)
          setError('게시글을 찾을 수 없습니다.')
        }
      } catch (error) {
        console.error('❌ 게시글 로드 오류:', error.message)
        setError('게시글을 불러오는 중 오류가 발생했습니다.')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchPost()
    }
  }, [id])

  // 샘플 데이터 (API 연결 실패 시 폴백용)
  const samplePosts = {
    1: {
      id: 1,
      title: '[공지] AR 대탈출 커뮤니티 이용 규칙 안내',
      content: `안녕하세요! AR 대탈출 커뮤니티를 이용해주셔서 감사합니다.

보다 건전하고 유익한 커뮤니티 환경을 위해 이용 규칙을 안내드립니다.

## 커뮤니티 이용 규칙

### 1. 기본 예의
- 상호 존중하는 대화 문화를 만들어주세요
- 비방, 욕설, 혐오 표현은 금지됩니다
- 건설적인 의견 교환을 지향해주세요

### 2. 게시글 작성 가이드
- 제목과 내용이 일치하도록 작성해주세요
- 적절한 카테고리를 선택해주세요
- 중복 게시물은 지양해주세요

### 3. 개인정보 보호
- 개인정보(전화번호, 주소 등)는 공개하지 마세요
- 타인의 개인정보를 무단으로 공유하지 마세요

### 4. 저작권 준수
- 타인의 저작물을 무단 사용하지 마세요
- 출처를 명확히 표기해주세요

위 규칙을 위반할 경우 게시물 삭제, 계정 제재 등의 조치가 있을 수 있습니다.

더 나은 커뮤니티를 만들어가는 데 함께해주세요! 🙏`,
      author: '운영진',
      createdAt: '2024-01-15T10:00:00Z',
      category: 'notice',
      views: 1250,
      likes: 45,
      comments: 12,
      isNotice: true
    },
    2: {
      id: 2,
      title: '새로운 AR 오브젝트 추가 제안 - 공룡 시리즈',
      content: `안녕하세요! 공룡을 주제로 한 AR 오브젝트들을 추가하면 어떨까요?

## 제안 내용

### 🦕 공룡 종류
- **티라노사우루스**: 가장 유명한 육식 공룡
- **트리케라톱스**: 뿔이 3개인 초식 공룡
- **브라키오사우루스**: 목이 긴 대형 초식 공룡
- **벨로시랩터**: 영리한 소형 육식 공룡

### 📚 교육적 효과
공룡 AR 오브젝트를 통해 아이들이 다음과 같은 것들을 학습할 수 있을 것 같아요:

1. **지질학적 시대** - 중생대에 대한 이해
2. **생물학적 특징** - 육식/초식, 크기, 서식지
3. **진화와 멸종** - 생물 진화의 역사
4. **화석과 고생물학** - 과학적 탐구 방법

### 🎮 게임 아이디어
- 공룡의 특징을 맞히는 퀴즈
- 공룡의 크기 비교하기
- 공룡이 살았던 시대 맞히기
- 공룡 화석 조각 맞추기

어떻게 생각하시나요? 많은 의견 부탁드려요! 🦴`,
      author: '공룡마니아',
      createdAt: '2024-01-14T16:30:00Z',
      category: 'idea',
      views: 580,
      likes: 23,
      comments: 8
    },
    3: {
      id: 3,
      title: '게임 중 앱이 종료되는 문제',
      content: `아이폰 14에서 문제 풀이 중간에 앱이 갑자기 종료되는 현상이 발생합니다.

## 문제 상황

### 📱 디바이스 정보
- **기종**: iPhone 14 Pro
- **iOS 버전**: 17.2
- **앱 버전**: 1.2.3

### 🚨 발생 상황
특히 AR 카메라 사용 중에 자주 발생하는 것 같아요. 구체적으로는:

1. **AR 스캔 중**: 객체를 스캔하다가 갑자기 앱 종료
2. **문제 풀이 중**: 선택지를 누르려는 순간 크래시
3. **결과 화면**: 정답 확인 후 다음 문제로 넘어갈 때

### 🔍 재현 방법
1. AR 대탈출 앱 실행
2. 아무 문제 세트나 선택
3. AR 카메라로 객체 스캔 시작
4. 2-3개 문제 풀고 나면 높은 확률로 크래시 발생

### 📊 빈도
- 하루에 3-4번 정도 발생
- 특히 오후 시간대에 더 자주 발생
- 배터리가 50% 이하일 때 더 빈번함

혹시 같은 문제를 겪고 계신 분들이 있나요? 임시 해결 방법이라도 있다면 공유해주세요! 🙏

### 🛠 시도해본 해결 방법
- 앱 재설치: 효과 없음
- 기기 재부팅: 일시적으로 나아지지만 곧 재발
- 다른 앱 종료: 약간의 개선 효과`,
      author: '버그헌터',
      createdAt: '2024-01-14T14:15:00Z',
      category: 'bug',
      views: 340,
      likes: 15,
      comments: 6
    },
    4: {
      id: 4,
      title: 'AR 대탈출로 아이들과 함께 공부하기',
      content: `초등학교 3학년 아이와 함께 AR 대탈출을 해보았는데 정말 재미있어하네요!

## 우리 아이의 반응

### 😍 가장 좋아하는 부분
- **AR 기술**: "와! 진짜 있는 것 같아요!" 하면서 신기해해요
- **과학 문제**: 특히 동물, 식물 관련 문제를 정말 좋아합니다
- **협력 플레이**: 엄마와 함께 문제를 풀 때 가장 즐거워해요

### 📚 교육적 효과를 실감했어요
특히 과학 문제들을 AR로 보면서 푸니까 이해도 잘하고, 기억도 오래 남는 것 같아요.

**예시:**
- 식물의 구조를 AR로 보면서 뿌리, 줄기, 잎의 역할을 배움
- 태양계 행성들의 크기 비교를 직접 체험
- 동물들의 서식지를 3D로 관찰

### 🎯 학습 효과
1. **집중력 향상**: 기존 문제집보다 훨씬 오래 집중해요
2. **기억력 증진**: AR로 본 내용은 며칠 뒤에도 기억해요
3. **호기심 자극**: "이것도 AR로 볼 수 있어요?" 하며 관심을 보여요

### 💡 부모님들께 추천하는 활용법

#### 함께 플레이하기
- 아이 혼자 하지 말고 옆에서 함께 참여하세요
- 틀린 문제는 왜 틀렸는지 함께 이야기해보세요
- 정답을 맞췄을 때는 크게 칭찬해주세요

#### 학습과 연계하기
- 학교에서 배운 내용과 연관된 문제 세트를 찾아보세요
- AR로 본 내용을 일상에서 다시 언급해주세요
- 관련 책이나 자료를 추가로 찾아보세요

### 🎈 재미있는 에피소드
어제는 공룡 문제를 풀다가 "엄마, 우리 집에도 티라노사우루스 있으면 좋겠어요!"라고 하더라고요. AR이 얼마나 실감나는지 알 수 있었어요 ㅋㅋ

같은 연령대 아이를 키우는 부모님들, AR 대탈출 정말 추천해요! 💕`,
      author: '학부모A',
      createdAt: '2024-01-13T19:45:00Z',
      category: 'free',
      views: 720,
      likes: 38,
      comments: 15
    },
    5: {
      id: 5,
      title: 'AR 대탈출 최고 점수 달성 팁 공유',
      content: `안녕하세요! 지난주에 드디어 10만점을 돌파했습니다! 🎉

## 고득점을 위한 전략

### 🎯 기본 전략

#### 1. 속도보다는 정확도
- 시간에 쫓겨서 급하게 답하지 마세요
- 한 번 틀리면 감점이 크니까 신중하게!
- AR 객체를 충분히 관찰한 후 답변하세요

#### 2. 카테고리별 특화 학습
- **과학**: 기본 개념을 확실히 알고 가세요
- **역사**: 연도와 인물을 연결해서 기억하세요
- **지리**: 지도를 많이 보고 위치를 외우세요
- **수학**: 공식보다는 개념 이해가 중요해요

### 🚀 고급 팁

#### AR 스캔 최적화
- **조명 체크**: 너무 어둡거나 밝으면 인식률이 떨어져요
- **거리 조절**: 객체에서 30-50cm 정도 떨어져서 스캔하세요
- **안정적인 손목**: 손 떨림 방지가 정확도 향상의 핵심!

#### 시간 관리
- **첫 10문제**: 워밍업 단계, 80% 정확도 목표
- **중반 20문제**: 본격적인 집중, 90% 정확도 목표  
- **마지막 10문제**: 체력 관리하며 신중하게

### 📊 점수 구조 이해하기

#### 기본 점수
- 정답: 100점
- 오답: -20점
- 시간 보너스: 남은 시간 × 2점

#### 연속 정답 보너스
- 5연속: +50점
- 10연속: +100점
- 15연속: +200점
- 20연속 이상: +500점

### 🏆 나만의 특별한 팁

#### 멘탈 관리
- 틀려도 당황하지 마세요! 다음 문제에 집중하세요
- 쉬운 문제일 때 방심하지 마세요
- 어려운 문제는 과감히 패스하는 것도 전략이에요

#### 환경 설정
- 조용한 곳에서 플레이하세요
- 충분한 공간 확보 (최소 2m × 2m)
- 배터리 충전 상태 확인 (AR은 배터리를 많이 써요)

### 🎁 추천 연습 방법

1. **데일리 챌린지**: 매일 조금씩이라도 꾸준히!
2. **약한 분야 집중**: 틀린 문제 유형을 분석해보세요
3. **친구와 경쟁**: 동기부여에 최고예요!

현재 최고 점수는 127,850점입니다! 다음 목표는 15만점 돌파예요 💪

혹시 다른 팁이나 질문이 있으시면 댓글로 알려주세요!`,
      author: '고수플레이어',
      createdAt: '2024-01-12T20:30:00Z',
      category: 'free',
      views: 1520,
      likes: 67,
      comments: 24
    },
    6: {
      id: 6,
      title: '친구들과 함께하는 재미있는 문제 추천',
      content: `친구들과 AR 대탈출을 할 때 가장 재미있는 문제 세트들을 추천해드려요!

## 🎪 파티 게임용 추천 문제

### 1. 동물 세계 탐험 🐅
- **난이도**: ★★☆☆☆
- **인원**: 2-4명
- **재미 포인트**: AR 동물들이 정말 살아있는 것 같아요!
- **추천 이유**: 모든 연령대가 함께 즐길 수 있어요

### 2. 우주 탐험대 🚀
- **난이도**: ★★★☆☆
- **인원**: 2-3명
- **재미 포인트**: 행성들의 크기 비교가 정말 놀라워요
- **추천 이유**: 과학 상식도 늘고 재미도 있어요

### 3. 역사 타임머신 ⏰
- **난이도**: ★★★★☆
- **인원**: 3-5명
- **재미 포인트**: 역사 속 인물들을 AR로 만날 수 있어요
- **추천 이유**: 팀워크가 중요한 문제들이 많아요

## 🏆 경쟁 게임용 추천

### 스피드 챌린지 ⚡
**규칙**: 누가 가장 빨리 정답을 맞히는지 경쟁
- 수학 연산 마스터
- 국기 맞히기 챌린지  
- 동물 소리 퀴즈

### 팀 대항전 👥
**규칙**: 2팀으로 나누어서 점수 대결
- 지리 탐험가 (세계 여행)
- 과학 실험실 (실험 도구들)
- 음식 월드컵 (세계 음식 맞히기)

## 💡 더 재미있게 즐기는 방법

### 특별 규칙 만들기
- **벌칙 게임**: 틀린 사람이 간식 사오기
- **힌트 시스템**: 모르면 친구가 몸짓으로 힌트 주기
- **시간 제한**: 30초 안에 답해야 하는 스피드 모드

### 역할 분담하기
- **스캐너**: AR 스캔 전담
- **씽커**: 문제 해결 전담  
- **타이머**: 시간 관리 담당
- **기록원**: 점수와 결과 기록

## 🎈 실제 후기

### 지난 주말 친구 집에서...
5명이 모여서 "동물 세계 탐험"을 했는데 정말 재밌었어요! 특히 호랑이가 나왔을 때 다들 깜짝 놀라서 소리를 질렀어요 ㅋㅋ

가장 웃겼던 순간은 친구가 코끼리를 보고 "하마!"라고 외쳤을 때였어요. AR로 보니까 정말 크더라고요!

### 생일파티에서 대성공! 🎂
동생 생일파티에서 AR 대탈출을 했는데 애들이 너무 좋아했어요. 특히 우주 탐험 문제에서 토성의 고리를 보고 다들 감탄했답니다.

## 🎯 연령대별 추천

### 초등학생 (8-12세)
- 동물 세계, 색깔 나라, 숫자 친구들
- 쉽고 재미있는 문제 위주

### 중고등학생 (13-18세)  
- 역사 타임머신, 과학 실험실, 지리 탐험
- 교과 과정과 연계된 문제들

### 대학생/성인 (19세+)
- 고급 수학, 세계사 깊이파기, 철학 사색
- 도전적이고 생각하게 만드는 문제들

혹시 다른 재미있는 문제 세트나 게임 방법을 아시는 분들은 댓글로 공유해주세요! 🙌`,
      author: '파티플래너',
      createdAt: '2024-01-11T15:20:00Z',
      category: 'free',
      views: 980,
      likes: 45,
      comments: 18
    },
    7: {
      id: 7,
      title: '과학 문제 정답률 높이는 방법',
      content: `과학 분야에서 정답률이 낮아서 고민이신 분들을 위한 실전 팁을 공유해드려요!

## 🧪 과학 영역별 공략법

### 생물학 마스터하기 🌱

#### 기본 원리 이해
- **세포 구조**: 동물세포 vs 식물세포 차이점 숙지
- **광합성**: 과정을 단계별로 이해하고 화학식 암기
- **유전**: 멘델의 법칙 기본 개념 정리

#### AR 활용 팁
- 현미경으로 보는 세포는 천천히 관찰하세요
- 식물 구조는 뿌리부터 잎까지 순서대로 확인
- 동물 분류는 특징을 하나씩 비교해보세요

### 물리학 정복하기 ⚡

#### 핵심 공식 암기
- **속력 = 거리 ÷ 시간**
- **밀도 = 질량 ÷ 부피**  
- **압력 = 힘 ÷ 넓이**

#### 개념 이해하기
- 힘의 종류와 방향성
- 에너지 보존 법칙
- 파동의 성질 (빛, 소리)

### 화학 완전정복 🧬

#### 원소 주기율표
- 주요 원소 20개는 반드시 암기
- 화학 기호와 원소명 매칭
- 원자량 개념 이해

#### 화학 반응
- 산과 염기의 성질
- 산화와 환원 반응
- 중화 반응의 원리

## 📚 효과적인 학습 전략

### 1단계: 기본 개념 정리
매일 30분씩 과학 기본서 읽기
- 교과서 내용을 요약 정리
- 모르는 용어는 따로 정리
- 그림과 도표를 자주 보기

### 2단계: AR 실전 연습  
- 틀린 문제는 반드시 복습
- 비슷한 유형의 문제 반복 풀이
- AR 객체의 세부사항까지 관찰

### 3단계: 약점 보완
- 정답률 낮은 분야 집중 공략
- 관련 유튜브 강의 시청
- 과학 다큐멘터리 보기

## 🎯 실전 문제 해결 요령

### AR 스캔 시 주의사항
1. **충분한 관찰 시간 확보**
   - 급하게 답하지 말고 2-3초 더 관찰
   - 객체를 여러 각도에서 살펴보기

2. **세부사항 놓치지 않기**
   - 색깔, 크기, 모양의 차이점 확인
   - 숫자나 기호가 있는지 체크

3. **배경 정보 활용**
   - 실험 도구의 용도 파악
   - 환경이나 상황 단서 찾기

### 문제 유형별 접근법

#### 실험 도구 문제
- 도구의 모양과 용도를 연결해서 기억
- 측정 단위까지 함께 암기
- 안전 주의사항도 함께 학습

#### 동식물 분류 문제
- 과(科), 목(目), 강(綱) 분류 체계 이해
- 특징적인 외형적 차이점 파악
- 서식지와 생활 습성도 고려

#### 물질의 성질 문제
- 물리적 성질 vs 화학적 성질 구분
- 상태 변화 조건 숙지
- 용해도, 밀도 등 수치 정보 암기

## 💪 학습 동기 유지법

### 성취감 쌓기
- 소목표 설정 (일주일에 과학 정답률 10% 향상)
- 성과 기록하기 (오답노트 작성)
- 스스로 칭찬하기

### 흥미 유발하기  
- 과학 관련 뉴스나 기사 읽기
- 과학관이나 박물관 견학
- 과학 실험 동영상 시청

### 동료 학습
- 스터디 그룹 만들기
- 서로 문제 출제해주기
- 어려운 개념 함께 토론하기

## 📈 1개월 후 결과

이 방법을 1개월간 실천한 결과:
- **생물학**: 45% → 78% (33% 향상!)
- **물리학**: 38% → 69% (31% 향상!)
- **화학**: 42% → 75% (33% 향상!)

꾸준히 하시면 분명히 실력이 늘어요! 화이팅! 💪`,
      author: '과학선생님',
      createdAt: '2024-01-10T14:30:00Z',
      category: 'free',
      views: 750,
      likes: 52,
      comments: 12
    },
    8: {
      id: 8,
      title: '새로운 업데이트 기대 포인트',
      content: `다음 업데이트에서 기대하는 새로운 기능들을 정리해보았어요!

## 🚀 가장 기대되는 신기능

### 1. 멀티플레이어 모드 👥
현재는 혼자서만 플레이할 수 있는데, 친구들과 실시간으로 같이 할 수 있다면 정말 재밌을 것 같아요!

**기대하는 기능:**
- 실시간 대전 모드
- 팀 협력 모드  
- 친구 초대 시스템
- 음성 채팅 기능

### 2. 커스텀 문제 제작 도구 🛠️
사용자가 직접 문제를 만들어서 공유할 수 있다면 콘텐츠가 무궁무진할 것 같아요.

**상상하는 기능:**
- 간단한 드래그 앤 드롭으로 문제 제작
- AR 객체 직접 업로드 가능
- 난이도 설정 기능
- 커뮤니티 평가 시스템

### 3. 학습 진도 관리 📊
개인별 맞춤 학습을 위한 상세한 통계 기능이 있으면 좋겠어요.

**원하는 정보:**
- 분야별 정답률 그래프
- 학습 시간 통계
- 취약점 분석 리포트
- 추천 학습 계획

## 🎮 게임플레이 개선 사항

### AR 기술 향상
- **인식 속도 개선**: 객체 스캔이 더 빨라지면 좋겠어요
- **안정성 향상**: 조명 변화에 더 강한 AR 기능
- **새로운 제스처**: 손가락 포인팅이나 터치 인터랙션

### UI/UX 업그레이드
- **다크 모드**: 눈이 편한 어두운 테마
- **접근성 개선**: 시각/청각 장애인을 위한 기능
- **다국어 지원**: 영어, 중국어, 일본어 버전

## 🎯 콘텐츠 확장 기대

### 새로운 주제 영역
1. **코딩/프로그래밍** 💻
   - 기본 코딩 개념
   - 알고리즘 시각화
   - 프로그래밍 언어 기초

2. **예술/문화** 🎨
   - 세계 명화 감상
   - 음악 이론 기초
   - 전통 문화 체험

3. **환경/생태** 🌍
   - 기후 변화 시뮬레이션
   - 생태계 보존 교육
   - 재활용 게임

### 시즌 이벤트
- **계절별 특별 문제**: 크리스마스, 추석 등
- **기념일 퀴즈**: 어린이날, 스승의 날 등
- **월드컵, 올림픽** 같은 특별 이벤트

## 🏆 리워드 시스템 업그레이드

### 성취 시스템
- **뱃지 수집**: 다양한 조건의 도전 과제
- **랭킹 시스템**: 전국, 지역, 학교별 순위
- **연속 출석**: 매일 플레이 보상

### 실제 혜택 연동
- **교육기관 제휴**: 성적 향상 시 할인 혜택
- **도서 구매 쿠폰**: 학습 관련 서적 할인
- **체험 활동 우선권**: 과학관, 박물관 등

## 🔧 기술적 개선사항

### 성능 최적화
- **배터리 사용량 감소**: 현재 너무 빨리 닳아요 😅
- **메모리 최적화**: 오래 플레이해도 끊김 없이
- **네트워크 효율성**: 데이터 사용량 최적화

### 호환성 확대
- **구형 기기 지원**: iPhone 6s, 갤럭시 S7 등
- **태블릿 최적화**: iPad에서 더 큰 화면으로
- **PC 버전**: 데스크톱에서도 플레이 가능

## 💭 개인적인 바람

가장 기대하는 건 **교육 현장과의 연계**예요. 실제 학교 수업에서 AR 대탈출을 활용할 수 있다면 정말 혁신적일 것 같아요!

선생님들이 수업 내용에 맞는 문제를 직접 만들어서 학생들과 함께 할 수 있고, 숙제나 시험 대신 AR 퀴즈로 평가할 수도 있고요.

상상만 해도 설레네요! 🤩

여러분은 어떤 업데이트를 가장 기대하시나요? 댓글로 의견 남겨주세요!`,
      author: '미래상상가',
      createdAt: '2024-01-09T16:45:00Z',
      category: 'free',
      views: 650,
      likes: 33,
      comments: 9
    },
    9: {
      id: 9,
      title: 'AR 기술이 교육에 미치는 영향',
      content: `AR 대탈출을 하면서 느낀 AR 기술의 교육적 가치에 대해 이야기해보고 싶어요.

## 🎓 전통 교육 vs AR 교육

### 기존 교육의 한계
- **일방향 소통**: 선생님 → 학생으로만 전달
- **추상적 개념**: 교과서 글자와 그림만으로는 이해가 어려움  
- **암기 위주**: 원리보다는 정답 맞히기에 집중
- **개별 차이 무시**: 모든 학생에게 동일한 방식 적용

### AR 교육의 장점
- **체험형 학습**: 직접 보고 만지면서 학습
- **시각적 이해**: 3차원 객체로 복잡한 개념도 쉽게
- **개인 맞춤**: 각자 속도에 맞는 학습 가능
- **흥미 유발**: 게임처럼 재미있어서 자연스럽게 집중

## 🧠 학습 효과의 과학적 근거

### 기억 메커니즘
**Hermann Ebbinghaus의 망각 곡선** 연구에 따르면:
- 단순 암기: 24시간 후 80% 망각
- 시각적 학습: 3일 후에도 65% 기억
- **체험형 학습**: 1주일 후에도 90% 기억 유지

### 다중 감각 활용
AR 학습은 여러 감각을 동시에 활용합니다:
- **시각**: 3D 객체 관찰
- **청각**: 음향 효과와 설명
- **촉각**: 디바이스 조작
- **공간 감각**: 실제 공간에서의 상호작용

## 📚 과목별 AR 교육 효과

### 과학 Science 🔬
**기존**: "DNA는 이중 나선 구조다"
**AR 후**: DNA를 360도 돌려보며 구조 체험

**효과**: 
- 분자 구조 이해도 300% 향상
- 화학 반응 과정 시각화
- 실험 안전사고 예방 훈련

### 역사 History 📿
**기존**: 교과서 속 흑백 사진
**AR 후**: 역사적 장소와 인물을 생생하게 체험

**효과**:
- 시대적 배경 이해도 증가
- 역사적 사건의 인과관계 파악
- 문화유산에 대한 관심 증대

### 수학 Mathematics ➕
**기존**: 추상적인 공식과 계산
**AR 후**: 기하학적 도형을 직접 조작하며 학습

**효과**:
- 공간 지각 능력 향상
- 함수 그래프 이해도 증가
- 수학적 사고력 발달

## 🌍 세계적인 AR 교육 동향

### 선진국 사례
**미국**: 
- 구글 Expeditions로 세계 여행 수업
- Microsoft HoloLens로 해부학 실습

**핀란드**: 
- AR 기반 언어 학습 프로그램
- 게임화된 수학 교육 시스템

**싱가포르**:
- 전국 초등학교 AR 과학 실험실 구축
- AI + AR 융합 개인 맞춤 교육

### 한국의 현황과 전망
**현재 상황**:
- 일부 혁신학교에서 시범 운영
- 사교육 시장에서 먼저 도입
- AR 대탈출 같은 교육 게임 인기

**미래 전망**:
- 2025년까지 전국 중학교 AR 교실 구축 계획
- 교사 AR 교육 연수 프로그램 확대
- 공교육과 사교육의 AR 격차 해소 노력

## 🚧 도전 과제와 해결 방안

### 기술적 장벽
**문제점**:
- 고가의 AR 장비
- 기술적 안정성 문제
- 교사들의 디지털 리터러시 부족

**해결 방안**:
- 정부 지원으로 장비 보급
- 클라우드 기반 AR 플랫폼 개발
- 교사 연수 프로그램 확대

### 교육적 우려
**우려사항**:
- 스크린 의존성 증가
- 기본기 소홀 가능성
- 사회적 격차 확대

**균형잡힌 접근**:
- AR과 전통 교육의 적절한 조화
- 디지털 디톡스 시간 확보
- 모든 학생에게 동등한 기회 제공

## 💡 AR 교육의 미래 비전

### 10년 후 상상해보기
- **가상 실험실**: 위험한 실험도 안전하게
- **시공간 초월**: 공룡 시대, 우주 여행 체험
- **AI 튜터**: 개인별 맞춤 AI 선생님
- **전세계 협업**: 다른 나라 학생들과 실시간 프로젝트

### 교육 패러다임의 변화
**From**: 지식 전달 → **To**: 경험 창조
**From**: 수동적 학습 → **To**: 능동적 탐구
**From**: 일괄 교육 → **To**: 개인 맞춤 학습
**From**: 평가 중심 → **To**: 성장 중심

## 🎯 결론: AR이 만드는 교육 혁명

AR 기술은 단순한 도구가 아닙니다. 교육의 본질을 바꾸는 혁명적 변화의 시작점이에요.

**궁극적 목표**는 모든 학생이:
- 배움의 즐거움을 느끼고
- 자신만의 속도로 성장하며  
- 창의적 사고력을 기르는 것

AR 대탈출을 통해 이런 가능성을 경험하고 있어서 정말 흥미롭습니다!

여러분은 AR 교육에 대해 어떻게 생각하시나요? 💭`,
      author: '교육공학자',
      createdAt: '2024-01-08T11:15:00Z',
      category: 'free',
      views: 580,
      likes: 41,
      comments: 15
    }
  }

  const sampleComments = {
    1: [
      {
        id: 1,
        content: '규칙 잘 읽었습니다! 좋은 커뮤니티가 될 수 있도록 노력하겠습니다.',
        author: '사용자1',
        createdAt: '2024-01-15T11:30:00Z',
        likes: 5
      },
      {
        id: 2,
        content: '명확한 가이드라인 감사합니다. 특히 저작권 관련 부분이 도움이 되었어요.',
        author: '사용자2',
        createdAt: '2024-01-15T14:20:00Z',
        likes: 3
      }
    ],
    2: [
      {
        id: 3,
        content: '공룡 아이디어 정말 좋네요! 특히 아이들이 좋아할 것 같아요. 저희 아이도 공룡을 너무 좋아하거든요.',
        author: '학부모A',
        createdAt: '2024-01-14T17:15:00Z',
        likes: 8
      },
      {
        id: 4,
        content: '티라노사우루스 AR로 보면 정말 무서울 것 같아요 ㅋㅋ 하지만 교육적으로는 정말 좋은 아이디어인 것 같습니다!',
        author: '공룡러버',
        createdAt: '2024-01-14T19:45:00Z',
        likes: 12
      }
    ],
    3: [
      {
        id: 5,
        content: '저도 같은 문제 겪고 있어요! iPhone 13에서도 비슷하게 발생합니다. 특히 배터리가 낮을 때 더 심한 것 같아요.',
        author: '같은증상',
        createdAt: '2024-01-14T15:30:00Z',
        likes: 7
      },
      {
        id: 6,
        content: '임시 해결책으로 AR 품질을 "보통"으로 낮추니까 조금 나아졌어요. 설정에서 한번 해보세요!',
        author: '도움러',
        createdAt: '2024-01-14T16:45:00Z',
        likes: 15
      },
      {
        id: 7,
        content: '개발진에서도 이 문제를 알고 있어서 다음 업데이트에서 수정될 예정이래요. 조금만 기다려보죠!',
        author: '베타테스터',
        createdAt: '2024-01-14T18:20:00Z',
        likes: 9
      }
    ],
    4: [
      {
        id: 8,
        content: '정말 부럽네요! 저도 아이와 함께 해봐야겠어요. 몇 살부터 시작하는 게 좋을까요?',
        author: '예비부모',
        createdAt: '2024-01-13T20:15:00Z',
        likes: 4
      },
      {
        id: 9,
        content: '우리 아이도 AR 대탈출 하면서 과학에 관심이 많아졌어요! 특히 우주 관련 문제를 좋아해요.',
        author: '우주맘',
        createdAt: '2024-01-13T21:30:00Z',
        likes: 6
      },
      {
        id: 10,
        content: '교육적 효과가 정말 좋은 것 같아요. 학원 대신 이런 걸로 공부하면 더 재밌을 것 같은데요.',
        author: '교육열심맘',
        createdAt: '2024-01-14T08:45:00Z',
        likes: 8
      }
    ],
    5: [
      {
        id: 11,
        content: '와! 10만점 축하해요! 저는 아직 7만점도 못 넘겼는데 팁 정말 도움 됐어요!',
        author: '초보플레이어',
        createdAt: '2024-01-12T21:15:00Z',
        likes: 12
      },
      {
        id: 12,
        content: '연속 정답 보너스가 그렇게 중요한 줄 몰랐네요! 앞으로는 더 신중하게 답해야겠어요.',
        author: '점수올리기',
        createdAt: '2024-01-12T22:30:00Z',
        likes: 8
      },
      {
        id: 13,
        content: '저는 15만점이 목표인데 정말 어렵네요 ㅠㅠ AR 스캔 최적화 팁 써보니까 확실히 나아진 것 같아요!',
        author: '고득점도전',
        createdAt: '2024-01-13T09:20:00Z',
        likes: 5
      },
      {
        id: 14,
        content: '멘탈 관리 부분이 정말 공감돼요. 틀리면 너무 당황해서 연달아 틀렸는데 이제 마음을 차분히 가져야겠어요.',
        author: '멘탈약함',
        createdAt: '2024-01-13T14:10:00Z',
        likes: 7
      }
    ],
    6: [
      {
        id: 15,
        content: '"동물 세계 탐험" 정말 재밌어요! 친구들이랑 했는데 다들 깜짝 놀라더라고요 ㅋㅋ',
        author: '동물러버',
        createdAt: '2024-01-11T16:30:00Z',
        likes: 9
      },
      {
        id: 16,
        content: '역할 분담 아이디어가 참신하네요! 다음에 친구들이랑 할 때 써보겠습니다 👍',
        author: '팀플레이어',
        createdAt: '2024-01-11T18:45:00Z',
        likes: 6
      },
      {
        id: 17,
        content: '벌칙 게임 ㅋㅋㅋ 진짜 재밌을 것 같아요! 틀린 사람이 간식 사오기 룰 써봐야겠어요.',
        author: '간식러버',
        createdAt: '2024-01-11T20:15:00Z',
        likes: 11
      }
    ],
    7: [
      {
        id: 18,
        content: '과학 정답률 올리기 팁 정말 감사해요! 특히 AR 스캔 시 주의사항 부분이 도움 됐어요.',
        author: '과학어려워',
        createdAt: '2024-01-10T15:45:00Z',
        likes: 8
      },
      {
        id: 19,
        content: '1개월 후 결과가 정말 놀라워요! 저도 꾸준히 해봐야겠어요. 동기부여 됐습니다 💪',
        author: '꾸준히할게요',
        createdAt: '2024-01-10T17:20:00Z',
        likes: 5
      },
      {
        id: 20,
        content: '과학 다큐멘터리 추천해주신 거 정말 좋은 아이디어네요! 어떤 걸 보면 좋을까요?',
        author: '다큐좋아',
        createdAt: '2024-01-10T19:30:00Z',
        likes: 3
      }
    ],
    8: [
      {
        id: 21,
        content: '멀티플레이어 모드 정말 기대돼요! 친구들이랑 실시간으로 경쟁할 수 있다면 최고일 것 같아요.',
        author: '멀티플레이어',
        createdAt: '2024-01-09T17:30:00Z',
        likes: 14
      },
      {
        id: 22,
        content: '커스텀 문제 제작 도구가 생기면 선생님들이 수업용으로 쓸 수 있겠네요! 정말 좋은 아이디어예요.',
        author: '선생님',
        createdAt: '2024-01-09T19:15:00Z',
        likes: 10
      },
      {
        id: 23,
        content: '다크 모드 제발 추가해주세요 ㅠㅠ 밤에 할 때 너무 눈이 부셔요...',
        author: '다크모드원해',
        createdAt: '2024-01-09T21:45:00Z',
        likes: 18
      }
    ],
    9: [
      {
        id: 24,
        content: 'AR 교육의 가능성에 대해 정말 잘 정리해주셨네요! 교육학과 학생으로서 많이 배웠습니다.',
        author: '교육학도',
        createdAt: '2024-01-08T12:30:00Z',
        likes: 7
      },
      {
        id: 25,
        content: '체험형 학습의 기억 보존 효과가 90%라니 정말 놀랍네요! 출처를 더 자세히 알 수 있을까요?',
        author: '연구자',
        createdAt: '2024-01-08T14:45:00Z',
        likes: 5
      },
      {
        id: 26,
        content: '우리나라도 빨리 AR 교육이 확산되면 좋겠어요. 아이들의 미래를 위해서라도 필요한 것 같아요.',
        author: '미래엄마',
        createdAt: '2024-01-08T16:20:00Z',
        likes: 9
      }
    ]
  }


  const handleBack = () => {
    navigate('/community')
  }

  const handleLike = async () => {
    if (!isLoggedIn) {
      alert('로그인이 필요한 기능입니다.')
      return
    }
    
    try {
      const response = await fetch(`/community/posts/${id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: user?.id // 로그인한 사용자 ID
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`)
      }
      
      const result = await response.json()
      if (result.success) {
        setLiked(result.liked)
        setLikeCount(result.likeCount)
      }
    } catch (error) {
      console.error('좋아요 처리 오류:', error)
      alert('좋아요 처리 중 오류가 발생했습니다.')
    }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.content.substring(0, 100) + '...',
          url: window.location.href
        })
      } catch (err) {
        console.log('공유 실패:', err)
      }
    } else {
      // 클립보드에 복사
      navigator.clipboard.writeText(window.location.href)
      alert('링크가 클립보드에 복사되었습니다!')
    }
  }

  const handleCommentLike = async (commentId) => {
    if (!isLoggedIn) {
      alert('로그인이 필요한 기능입니다.')
      return
    }
    
    try {
      // 직접 데이터베이스 로직으로 처리 (API가 문제가 있으므로)
      console.log('댓글 좋아요 토글:', commentId)
      
      // 댓글의 현재 좋아요 상태를 찾기
      const targetComment = comments.find(c => c.comment_id == commentId)
      if (!targetComment) return
      
      const isCurrentlyLiked = targetComment.is_liked
      const currentLikeCount = parseInt(targetComment.likes_count) || 0
      
      // 서버에 API 호출
      const response = await fetch(`/community/comments/${commentId}/like`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const result = await response.json()

      if (!result.success) {
        throw new Error(result.message || '댓글 좋아요 처리 실패')
      }

      console.log('댓글 좋아요 API 성공:', result)

      // UI를 서버 응답에 맞춰 업데이트
      setComments(prev => prev.map(comment => {
        if (comment.comment_id == commentId) {
          return {
            ...comment,
            is_liked: result.isLiked,
            likes_count: isCurrentlyLiked ? currentLikeCount - 1 : currentLikeCount + 1
          }
        }
        return comment
      }))
      
    } catch (error) {
      console.error('댓글 좋아요 처리 오류:', error)
      alert('댓글 좋아요 처리 중 오류가 발생했습니다.')
      
      // 오류 발생 시 UI 롤백
      setComments(prev => prev.map(comment => {
        if (comment.comment_id == commentId) {
          const isCurrentlyLiked = comment.is_liked
          const currentLikeCount = parseInt(comment.likes_count) || 0
          return {
            ...comment,
            is_liked: !isCurrentlyLiked,
            likes_count: isCurrentlyLiked ? currentLikeCount + 1 : currentLikeCount - 1
          }
        }
        return comment
      }))
    }
  }

  const handleCommentSubmit = async () => {
    if (!isLoggedIn) {
      alert('로그인이 필요한 기능입니다.')
      return
    }

    if (!newComment.trim()) {
      alert('댓글 내용을 입력해주세요.')
      return
    }

    setSubmittingComment(true)
    
    try {
      const response = await fetch(`/community/posts/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          content: newComment.trim(),
          user_id: user?.id // 로그인한 사용자 ID
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`)
      }
      
      const result = await response.json()
      if (result.success) {
        setComments(prev => [...prev, result.comment])
        setNewComment('')
        // 댓글 수 업데이트
        setPost(prev => ({
          ...prev,
          comment_count: (parseInt(prev.comment_count) + 1).toString()
        }))
      } else {
        throw new Error(result.message || '댓글 작성에 실패했습니다.')
      }
    } catch (err) {
      console.error('댓글 작성 오류:', err)
      alert('댓글 작성 중 오류가 발생했습니다.')
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleReplyClick = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId)
    setReplyContent('')
  }

  const handleReplySubmit = async (commentId) => {
    if (!isLoggedIn) {
      alert('로그인이 필요한 기능입니다.')
      return
    }

    if (!replyContent.trim()) {
      alert('답글 내용을 입력해주세요.')
      return
    }

    setSubmittingReply(true)
    
    try {
      const response = await fetch(`/community/posts/${id}/comments/${commentId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          content: replyContent.trim(),
          user_id: user?.id // 로그인한 사용자 ID
        })
      })
      
      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}`)
      }
      
      const result = await response.json()
      if (result.success) {
        setComments(prev => [...prev, result.comment])
        setReplyContent('')
        setReplyingTo(null)
        // 댓글 수 업데이트
        setPost(prev => ({
          ...prev,
          comment_count: (parseInt(prev.comment_count) + 1).toString()
        }))
      } else {
        throw new Error(result.message || '답글 작성에 실패했습니다.')
      }
    } catch (err) {
      console.error('답글 작성 오류:', err)
      alert('답글 작성 중 오류가 발생했습니다.')
    } finally {
      setSubmittingReply(false)
    }
  }

  // 댓글을 계층구조로 정리하는 함수
  const organizeComments = (comments) => {
    const commentMap = {}
    const rootComments = []
    
    // 먼저 모든 댓글을 ID로 매핑
    comments.forEach(comment => {
      commentMap[comment.comment_id] = {
        ...comment,
        replies: []
      }
    })
    
    // 부모-자식 관계 설정
    comments.forEach(comment => {
      if (comment.parent_comment_id && commentMap[comment.parent_comment_id]) {
        commentMap[comment.parent_comment_id].replies.push(commentMap[comment.comment_id])
      } else {
        rootComments.push(commentMap[comment.comment_id])
      }
    })
    
    return rootComments
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    
    // 한국 시간으로 변환 (UTC+9)
    const koreaDate = new Date(date.getTime() + (9 * 60 * 60 * 1000))
    const koreaNow = new Date(now.getTime() + (9 * 60 * 60 * 1000))
    
    // 날짜만 비교 (시간 제외)
    const dateOnly = new Date(koreaDate.getFullYear(), koreaDate.getMonth(), koreaDate.getDate())
    const nowOnly = new Date(koreaNow.getFullYear(), koreaNow.getMonth(), koreaNow.getDate())
    
    const diffTime = nowOnly - dateOnly
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) {
      // 오늘 작성된 게시글은 시간 표시
      return koreaDate.toLocaleTimeString('ko-KR', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      })
    }
    if (diffDays === 1) return '어제'
    if (diffDays < 7) return `${diffDays}일 전`
    return koreaDate.toLocaleDateString('ko-KR')
  }

  const handleMoreClick = (event) => {
    console.log('=== 더보기 버튼 클릭 ===')
    console.log('Current showDropdown state:', showDropdown)
    console.log('Event:', event)
    
    event.stopPropagation()
    const newShowDropdown = !showDropdown
    setShowDropdown(newShowDropdown)
    
    console.log('Setting showDropdown to:', newShowDropdown)
    console.log('Dropdown should now be:', newShowDropdown ? 'visible' : 'hidden')
  }

  const handleEditPost = (event) => {
    console.log('=== 수정 버튼 클릭 ===')
    console.log('Event:', event)
    console.log('Current post data:', post)
    
    event.stopPropagation()
    
    // 현재 게시글 정보로 수정 모달 초기화
    const title = post.title || ''
    const content = post.content || ''
    const category = post.category || 'free'
    
    console.log('Setting edit modal data:', { title, content, category })
    
    setEditTitle(title)
    setEditContent(content)
    setEditCategory(category)
    setShowEditModal(true)
    setShowDropdown(false)
    
    console.log('Edit modal should now be visible')
    console.log('showEditModal state will be:', true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditTitle('')
    setEditContent('')
    setEditCategory('')
  }

  const handleUpdatePost = async () => {
    console.log('=== 게시글 수정 시작 ===')
    console.log('Edit data:', { 
      title: editTitle, 
      content: editContent.substring(0, 100) + '...', 
      category: editCategory 
    })
    
    if (!editTitle.trim()) {
      console.log('❌ 제목이 비어있음')
      alert('제목을 입력해주세요.')
      return
    }
    
    if (!editContent.trim()) {
      console.log('❌ 내용이 비어있음')
      alert('내용을 입력해주세요.')
      return
    }

    console.log('✅ 입력 검증 통과')
    setUpdatingPost(true)
    
    try {
      const requestData = {
        title: editTitle.trim(),
        content: editContent.trim(),
        category: editCategory,
        user_id: user?.id // 로그인한 사용자 ID
      }
      
      console.log('📡 API 요청 데이터:', requestData)
      console.log('📡 요청 URL:', `/community/posts/${post.post_id}`)
      
      const response = await fetch(`/community/posts/${post.post_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestData)
      })
      
      console.log('📡 응답 상태:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ HTTP 오류 응답:', errorText)
        throw new Error(`HTTP 오류: ${response.status} - ${errorText}`)
      }
      
      const result = await response.json()
      console.log('📡 서버 응답:', result)
      
      if (result.success) {
        console.log('✅ 수정 성공, 게시글 데이터 업데이트 중...')
        
        // 게시글 정보 업데이트 - 서버에서 반환된 데이터 사용
        const updatedPost = {
          ...post,
          title: result.post?.title || editTitle.trim(),
          content: result.post?.content || editContent.trim(),
          category: result.post?.category || editCategory,
          updated_at: result.post?.updated_at || new Date().toISOString()
        }
        
        console.log('📝 업데이트된 게시글 데이터:', updatedPost)
        setPost(updatedPost)
        
        // 모달 상태 초기화
        setShowEditModal(false)
        setEditTitle('')
        setEditContent('')
        setEditCategory('')
        
        console.log('✅ 모든 업데이트 완료')
        alert('게시글이 수정되었습니다.')
      } else {
        console.error('❌ 서버에서 실패 응답:', result.message)
        throw new Error(result.message || '게시글 수정에 실패했습니다.')
      }
    } catch (err) {
      console.error('❌ 게시글 수정 오류:', err)
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      })
      alert('게시글 수정 중 오류가 발생했습니다: ' + err.message)
    } finally {
      console.log('🔄 수정 완료, 로딩 상태 해제')
      setUpdatingPost(false)
    }
  }

  const handleDeletePost = async (event) => {
    console.log('=== 삭제 버튼 클릭 ===')
    console.log('Event:', event)
    
    event.stopPropagation()
    
    const confirmDelete = window.confirm('게시글을 삭제하시겠습니까?')
    console.log('사용자 삭제 확인:', confirmDelete)
    
    if (!confirmDelete) {
      console.log('삭제 취소됨')
      setShowDropdown(false)
      return
    }
    
    console.log('삭제 진행 시작...')
    
    try {
      console.log('📡 삭제 API 호출:', {
        postId: post.post_id,
        url: `/community/posts/${post.post_id}`
      })
      
      const response = await fetch(`/community/posts/${post.post_id}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      
      console.log('📡 삭제 API 응답:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ 삭제 API 오류 응답:', errorText)
        throw new Error(`HTTP 오류: ${response.status} - ${errorText}`)
      }
      
      const result = await response.json()
      console.log('📡 삭제 API 결과:', result)
      
      if (result.success) {
        console.log('✅ 삭제 성공, 커뮤니티 페이지로 이동')
        alert('게시글이 삭제되었습니다.')
        navigate('/community')
      } else {
        console.error('❌ 삭제 실패:', result.message)
        throw new Error(result.message || '게시글 삭제에 실패했습니다.')
      }
    } catch (error) {
      console.error('❌ 게시글 삭제 오류:', error)
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      })
      alert('게시글 삭제 중 오류가 발생했습니다: ' + error.message)
    }
    
    setShowDropdown(false)
  }

  // 댓글 렌더링 함수
  const renderComment = (comment, isReply = false) => {
    return (
      <div key={comment.comment_id}>
        {isReply ? (
          <ReplyItem theme={theme}>
            <CommentHeader>
              <CommentAuthor theme={theme}>
                <Users size={14} />
                {comment.username || comment.user_name || `사용자 ${comment.user_id}`}
              </CommentAuthor>
              <CommentMeta theme={theme}>
                <span>{formatDate(comment.created_at)}</span>
              </CommentMeta>
            </CommentHeader>
            
            <CommentContent theme={theme}>
              {comment.content}
            </CommentContent>
            
            <CommentActions>
              <CommentActionButton 
                theme={theme} 
                className={comment.is_liked ? 'liked' : ''}
                onClick={() => handleCommentLike(comment.comment_id)}
              >
                <ThumbsUp size={12} fill={comment.is_liked ? 'currentColor' : 'none'} />
                좋아요 {comment.likes_count || 0}
              </CommentActionButton>
            </CommentActions>
          </ReplyItem>
        ) : (
          <CommentItem theme={theme}>
            <CommentHeader>
              <CommentAuthor theme={theme}>
                <Users size={14} />
                {comment.username || comment.user_name || `사용자 ${comment.user_id}`}
              </CommentAuthor>
              <CommentMeta theme={theme}>
                <span>{formatDate(comment.created_at)}</span>
              </CommentMeta>
            </CommentHeader>
            
            <CommentContent theme={theme}>
              {comment.content}
            </CommentContent>
            
            <CommentActions>
              <CommentActionButton 
                theme={theme} 
                className={comment.is_liked ? 'liked' : ''}
                onClick={() => handleCommentLike(comment.comment_id)}
              >
                <ThumbsUp size={12} fill={comment.is_liked ? 'currentColor' : 'none'} />
                좋아요 {comment.likes_count || 0}
              </CommentActionButton>
              <CommentActionButton 
                theme={theme}
                onClick={() => handleReplyClick(comment.comment_id)}
              >
                답글
              </CommentActionButton>
            </CommentActions>
            
            {replyingTo === comment.comment_id && (
              <ReplySection theme={theme}>
                <ReplyInput
                  theme={theme}
                  placeholder="답글을 입력하세요..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                />
                <ReplyActions>
                  <ReplyButton 
                    theme={theme}
                    className="secondary"
                    onClick={() => setReplyingTo(null)}
                  >
                    취소
                  </ReplyButton>
                  <ReplyButton 
                    theme={theme}
                    className="primary"
                    disabled={submittingReply || !replyContent.trim()}
                    onClick={() => handleReplySubmit(comment.comment_id)}
                  >
                    {submittingReply ? '답글 작성 중...' : '답글 작성'}
                  </ReplyButton>
                </ReplyActions>
              </ReplySection>
            )}
            
            {comment.replies && comment.replies.length > 0 && (
              <RepliesContainer theme={theme}>
                {comment.replies.map(reply => renderComment(reply, true))}
              </RepliesContainer>
            )}
          </CommentItem>
        )}
      </div>
    )
  }

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown) {
        setShowDropdown(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown])

  if (loading) {
    return (
      <Container theme={theme}>
        <LoadingSpinner theme={theme}>
          게시글을 불러오는 중...
        </LoadingSpinner>
      </Container>
    )
  }

  if (error || !post) {
    return (
      <Container theme={theme}>
        <ErrorMessage theme={theme}>
          <h3>오류가 발생했습니다</h3>
          <p>{error}</p>
          <BackButton theme={theme} onClick={handleBack}>
            <ArrowLeft size={16} />
            목록으로 돌아가기
          </BackButton>
        </ErrorMessage>
      </Container>
    )
  }

  return (
    <Container theme={theme}>
      <Header>
        <BackButton theme={theme} onClick={handleBack}>
          <ArrowLeft size={16} />
          목록으로
        </BackButton>
      </Header>

      <PostContainer theme={theme}>
        <PostHeader theme={theme}>
          <PostTitle theme={theme}>
            {post.is_pinned && <Pin size={20} />}
            {post.title}
            {post.is_pinned && <PostBadge $type="notice">공지</PostBadge>}
            {post.category === 'IDEA' && <PostBadge $type="idea">아이디어</PostBadge>}
            {post.category === 'BUG_REPORT' && <PostBadge $type="bug">버그신고</PostBadge>}
            {post.category === 'FREE_BOARD' && <PostBadge $type="free">자유</PostBadge>}
            {post.like_count > 20 && <PostBadge $type="hot">HOT</PostBadge>}
          </PostTitle>
          
          <PostMeta>
            <MetaLeft theme={theme}>
              <MetaItem>
                <Users size={14} />
                {post.username || `사용자 ${post.user_id}`}
              </MetaItem>
              <MetaItem>
                <Clock size={14} />
                {formatDate(post.created_at)}
              </MetaItem>
              <MetaItem>
                <Eye size={14} />
                {post.view_count || 0}
              </MetaItem>
            </MetaLeft>
            
            <MetaRight>
              <ActionButton theme={theme} onClick={handleMoreClick}>
                <MoreHorizontal size={14} />
              </ActionButton>
              {showDropdown && (
                <>
                  {console.log('🎯 드롭다운 메뉴 렌더링 중...', { showDropdown })}
                  <DropdownMenu theme={theme}>
                    <DropdownItem theme={theme} onClick={handleEditPost}>
                      <Edit size={16} />
                      수정
                    </DropdownItem>
                    <DropdownItem theme={theme} className="delete" onClick={handleDeletePost}>
                      <Trash2 size={16} />
                      삭제
                    </DropdownItem>
                  </DropdownMenu>
                </>
              )}
            </MetaRight>
          </PostMeta>
        </PostHeader>

        <PostContent theme={theme}>
          {post.content.split('\n\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </PostContent>

        <PostActions theme={theme}>
          <ActionsLeft>
            <LikeButton 
              theme={theme} 
              $liked={liked}
              onClick={handleLike}
            >
              <Heart size={16} fill={liked ? 'currentColor' : 'none'} />
              좋아요 {likeCount}
            </LikeButton>
            
            <ShareButton theme={theme} onClick={handleShare}>
              <Share2 size={16} />
              공유
            </ShareButton>
          </ActionsLeft>
        </PostActions>
      </PostContainer>

      <CommentsSection theme={theme}>
        <CommentsHeader theme={theme}>
          <CommentsTitle theme={theme}>
            <MessageCircle size={20} />
            댓글 {comments.length}개
          </CommentsTitle>
        </CommentsHeader>

        {isLoggedIn && (
          <CommentWriteSection theme={theme}>
            <CommentTextarea
              theme={theme}
              placeholder="댓글을 입력해주세요..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              maxLength={500}
            />
            <CommentSubmitButton 
              onClick={handleCommentSubmit}
              disabled={!newComment.trim() || submittingComment}
            >
              {submittingComment ? '작성 중...' : (
                <>
                  <Send size={14} />
                  댓글 작성
                </>
              )}
            </CommentSubmitButton>
          </CommentWriteSection>
        )}

        <CommentsList theme={theme}>
          {comments.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: theme.textSecondary 
            }}>
              첫 번째 댓글을 작성해보세요!
            </div>
          ) : (
            organizeComments(comments).map(comment => renderComment(comment))
          )}
        </CommentsList>
      </CommentsSection>
      
      {/* 수정 모달 */}
      {showEditModal && (
        <>
          {console.log('🎨 수정 모달 렌더링 중...', { showEditModal, editTitle, editContent, editCategory })}
          <ModalOverlay onClick={handleCloseEditModal}>
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ModalTitle theme={theme}>게시글 수정</ModalTitle>
              <ModalCloseButton theme={theme} onClick={handleCloseEditModal}>
                <X size={20} />
              </ModalCloseButton>
            </ModalHeader>
            
            <ModalBody>
              <FormGroup>
                <FormLabel theme={theme}>카테고리</FormLabel>
                <FormSelect 
                  theme={theme}
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                >
                  <option value="free">자유</option>
                  <option value="question">질문</option>
                  <option value="suggestion">건의</option>
                  <option value="bug">버그신고</option>
                  <option value="notice">공지</option>
                </FormSelect>
              </FormGroup>
              
              <FormGroup>
                <FormLabel theme={theme}>제목</FormLabel>
                <FormInput
                  theme={theme}
                  type="text"
                  placeholder="제목을 입력하세요"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel theme={theme}>내용</FormLabel>
                <FormTextarea
                  theme={theme}
                  placeholder="내용을 입력하세요"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                />
              </FormGroup>
            </ModalBody>
            
            <ModalFooter theme={theme}>
              <ModalButton 
                theme={theme}
                className="secondary"
                onClick={handleCloseEditModal}
              >
                취소
              </ModalButton>
              <ModalButton 
                theme={theme}
                className="primary"
                disabled={updatingPost || !editTitle.trim() || !editContent.trim()}
                onClick={() => {
                  console.log('🔍 수정하기 버튼 클릭됨!');
                  console.log('🔍 버튼 상태 확인:', {
                    updatingPost,
                    editTitleTrimmed: editTitle.trim(),
                    editContentTrimmed: editContent.trim(),
                    isDisabled: updatingPost || !editTitle.trim() || !editContent.trim()
                  });
                  handleUpdatePost();
                }}
              >
                {updatingPost ? '수정 중...' : '수정하기'}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
        </>
      )}
    </Container>
  )
}